import React, { useMemo, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { ReactComponent as Segmentation_people } from '../Layer_images/Settings_images/segmentation_people.svg';
import { UI_Table } from '../ui_components/ui-table';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

// Custom Tooltip Component
const CustomTooltip = ({ data, position, visible }) => {
    if (!visible || !data) return null;

    return (
        <div 
            className="custom-tooltip"
            style={{
                position: 'absolute',
                left: position.x + 10,
                top: position.y - 10,
                background: '#333',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '4px',
                fontSize: '12px',
                zIndex: 1000,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                maxWidth: '200px'
            }}
        >
            {data.type === 'gender' && (
                <div>
                    <div><strong>Gender Distribution</strong></div>
                    <div>Male: {data.male}%</div>
                    <div>Female: {data.female}%</div>
                </div>
            )}
            {data.type === 'race' && (
                <div>
                    <div><strong>Race Breakdown</strong></div>
                    {data.races.map((race, index) => (
                        <div key={index}>{race.label}: {race.percentage}%</div>
                    ))}
                </div>
            )}
        </div>
    );
};

export function OverviewVisitorsDemographic({ analyticsData }) {
    const [tooltip, setTooltip] = useState({ visible: false, data: null, position: { x: 0, y: 0 } });

    // Cache processing results using useMemo
    const processedData = useMemo(() => {
        if (!analyticsData?.current) {
            return {
                avgFamilySize: 0,
                avgNetWorth: 0,
                genderData: { male: 0, female: 0 },
                raceData: [],
                incomeData: [],
                ageData: [],
                storeVisitsData: []
            };
        }
        const currentData = analyticsData.current;
        
        // Calculate totals for percentages
        const genderTotal = currentData.gender_male + currentData.gender_female;
        const raceTotal = currentData.race_white + currentData.race_asian + 
                         currentData.race_black + currentData.race_hispanic + 
                         currentData.race_other;
        const incomeTotal = currentData.income_low + currentData.income_lower_middle + 
                           currentData.income_middle + currentData.income_upper_middle + 
                           currentData.income_high + currentData.income_very_high;
        const ageTotal = currentData.age_under_18 + currentData.age_18_24 + 
                        currentData.age_25_34 + currentData.age_35_44 + 
                        currentData.age_45_54 + currentData.age_55_64 + 
                        currentData.age_65_plus;
        
        // Gender percentages
        const genderData = {
            male: genderTotal > 0 ? Math.round((currentData.gender_male / genderTotal) * 100) : 0,
            female: genderTotal > 0 ? Math.round((currentData.gender_female / genderTotal) * 100) : 0
        };
        
        // Race data - sorted by highest value
        const raceEntries = [
            { key: 'white', value: currentData.race_white, label: 'White' },
            { key: 'asian', value: currentData.race_asian, label: 'Asian' },
            { key: 'black', value: currentData.race_black, label: 'Black/African American' },
            { key: 'hispanic', value: currentData.race_hispanic, label: 'Hispanic/Latino' },
            { key: 'other', value: currentData.race_other, label: 'Other' }
        ];
        
        const raceData = raceEntries
            .sort((a, b) => b.value - a.value)
            .map(item => ({
                ...item,
                percentage: raceTotal > 0 ? Math.round((item.value / raceTotal) * 100) : 0
            }));
        
        // Income data - ordered by income level
        const incomeEntries = [
            { key: 'low', value: currentData.income_low, label: 'Low Income' },
            { key: 'lower_middle', value: currentData.income_lower_middle, label: 'Lower Middle' },
            { key: 'middle', value: currentData.income_middle, label: 'Middle Income' },
            { key: 'upper_middle', value: currentData.income_upper_middle, label: 'Upper Middle' },
            { key: 'high', value: currentData.income_high, label: 'High Income' },
            { key: 'very_high', value: currentData.income_very_high, label: 'Very High Income' }
        ];
        
        const incomeData = incomeEntries.map(item => ({
            ...item,
            percentage: incomeTotal > 0 ? Math.round((item.value / incomeTotal) * 100) : 0
        }));
        
        // Age data - ordered by age
        const ageEntries = [
            // { key: 'under_18', value: currentData.age_under_18, label: 'Under 18' },
            { key: '18_24', value: currentData.age_18_24, label: '18-24' },
            { key: '25_34', value: currentData.age_25_34, label: '25-34' },
            { key: '35_44', value: currentData.age_35_44, label: '35-44' },
            { key: '45_54', value: currentData.age_45_54, label: '45-54' },
            { key: '55_64', value: currentData.age_55_64, label: '55-64' },
            { key: '65_plus', value: currentData.age_65_plus, label: '65+' }
        ];
        
        const ageData = ageEntries.map(item => ({
            ...item,
            percentage: ageTotal > 0 ? Math.round((item.value / ageTotal) * 100) : 0
        }));
        
        // Store visits data
        const storeVisitsData = currentData.storeVisitsData.map((e) => ({...e, _id: e.title})) || [];
        
        return {
            avgFamilySize: currentData.avg_family_size || 0,
            avgNetWorth: currentData.avg_net_worth || 0,
            genderData,
            raceData,
            incomeData,
            ageData,
            storeVisitsData
        };
    }, [analyticsData]);

    // Handle mouse events for tooltips
    const handleMouseMove = (event, data) => {
        setTooltip({
            visible: true,
            data,
            position: { x: event.clientX, y: event.clientY }
        });
    };

    const handleMouseLeave = () => {
        setTooltip({ visible: false, data: null, position: { x: 0, y: 0 } });
    };

    // Gender chart data
    const genderChartData = {
        labels: ['Gender Distribution'],
        datasets: [
            {
                label: 'Male',
                data: [processedData.genderData.male],
                backgroundColor: 'rgba(173, 216, 230, 1)',
                borderColor: 'rgba(173, 216, 230, 1)',
                borderWidth: 1,
                barPercentage: 1,
                categoryPercentage: 1,
            },
            {
                label: 'Female',
                data: [processedData.genderData.female],
                backgroundColor: 'rgba(30, 144, 255, 1)',
                borderColor: 'rgba(30, 144, 255, 1)',
                borderWidth: 1,
                barPercentage: 1,
                categoryPercentage: 1,
            },
        ],
    };

    // Race chart data with percentage labels
    const raceChartData = {
        labels: processedData.raceData.map(item => `${item.label} (${item.percentage}%)`),
        datasets: [
            {
                data: processedData.raceData.map(item => item.percentage),
                backgroundColor: '#3b82f6',
                barThickness: 20,
                borderRadius: 4,
            },
        ],
    };

    // Income chart data
    const incomeChartData = {
        labels: processedData.incomeData.map(item => item.label),
        datasets: [
            {
                data: processedData.incomeData.map(item => item.percentage),
                backgroundColor: 'rgba(173, 216, 230, 1)',
            },
        ],
    };

    // Age chart data
    const ageChartData = {
        labels: processedData.ageData.map(item => item.label),
        datasets: [
            {
                data: processedData.ageData.map(item => item.percentage),
                backgroundColor: '#6366f1',
            },
        ],
    };

    // Chart options
    const optionsGender = {
        indexAxis: 'y',
        scales: {
            x: { 
                stacked: true, 
                grid: { display: false }, 
                ticks: { display: false }, 
                border: { display: false } 
            },
            y: { 
                stacked: true, 
                grid: { display: false }, 
                ticks: { display: false }, 
                border: { display: false } 
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                align: 'end',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    boxWidth: 8,
                    boxHeight: 8,
                },
            },
            tooltip: {
                enabled: false,
            },
            datalabels: {
                display: true,
                color: (context) => context.datasetIndex === 0 ? '#555' : 'white',
                formatter: (value) => `${value}%`,
                font: {
                    weight: 'bold',
                },
                align: 'center',
                anchor: 'center',
            },
        },
    };

    const optionsHorizontalBar = {
        indexAxis: 'y',
        scales: {
            x: {
                beginAtZero: true,
                grid: { display: false },
                ticks: { 
                    display: true,
                    callback: function(value) {
                        return value + '%';
                    }
                },
                border: { display: false },
            },
            y: {
                grid: { display: false },
                border: { display: false },
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { 
                enabled: true,
                callbacks: {
                    label: function(context) {
                        return context.parsed.x + '%';
                    }
                }
            },
        },
        layout: {
            padding: 0,
        },
    };

    const optionsVerticalBar = {
        scales: {
            x: {
                grid: { display: false },
                border: { display: false },
            },
            y: {
                beginAtZero: true,
                grid: { display: true },
                ticks: { 
                    display: true,
                    callback: function(value) {
                        return value + '%';
                    }
                },
                border: { display: true },
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { 
                enabled: true,
                callbacks: {
                    label: function(context) {
                        return context.parsed.y + '%';
                    }
                }
            },
        },
        layout: {
            padding: 0,
        },
    };

    function formatNumber(value, round=false) {
        if(!value) return 'No Data'
        
        if (typeof value === 'number' || (typeof value === 'string' && !isNaN(value))) {
            if(round){ 
                value = Math.round(value * 100) / 100;
            }else{
                value = '$' + Math.round(value)
            }
            return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        } else {
            return value;
        }
    }

    return (
        <div style={{ position: 'relative' }}>
            <h2 className='mt-20 mb-20 font-weight600 data-component'>Visitors Demographic</h2>
            <div className='display-flex gap-10 mtb-10 sidbar-primary-text mobile-flex-direction-column'>
                <div className="box-container" style={{ flex: 1 }}>
                    <div className='segmentation-top-sidebar'>
                        <div className='display-inline-block pt-5'><Segmentation_people /></div>
                        <div className='display-inline-block font-size-10'>Average Family Size</div>
                    </div>
                    <div className='text-align-left pl-5'>{formatNumber(processedData.avgFamilySize, true)}</div>
                </div>
                <div className="box-container" style={{ flex: 1 }}>
                    <div className='segmentation-top-sidebar'>
                        <div className='display-inline-block pt-5'>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M13.36 4.54679L9.51999 1.86012C8.47333 1.12679 6.86666 1.16679 5.85999 1.94679L2.51999 4.55345C1.85333 5.07345 1.32666 6.14012 1.32666 6.98012V11.5801C1.32666 13.2601 2.70666 14.6668 4.40666 14.6668H11.5933C13.2933 14.6668 14.6733 13.2868 14.6733 11.5868V7.06679C14.6733 6.16679 14.0933 5.06012 13.36 4.54679ZM8.49999 12.0001C8.49999 12.2735 8.27333 12.5001 7.99999 12.5001C7.72666 12.5001 7.49999 12.2735 7.49999 12.0001V10.0001C7.49999 9.72679 7.72666 9.50012 7.99999 9.50012C8.27333 9.50012 8.49999 9.72679 8.49999 10.0001V12.0001Z" fill="#A4A7AE" />
                            </svg>
                        </div>
                        <div className='display-inline-block font-size-10'>Average Net Worth</div>
                    </div>
                    <div className='text-align-left pl-5'>{formatNumber(processedData.avgNetWorth)}</div>
                </div>
                <div 
                    className="box-container" 
                    style={{ flex: 2, height: 60, paddingRight: 10, cursor: 'pointer' }}
                    onMouseMove={(e) => handleMouseMove(e, {
                        type: 'gender',
                        male: processedData.genderData.male,
                        female: processedData.genderData.female
                    })}
                    onMouseLeave={handleMouseLeave}
                >
                    <div>
                        <div className='segmentation-top-sidebar' style={{ position: 'absolute' }}>
                            <div className='display-inline-block pt-5'>
                                <svg height="16px" width="16px" version="1.1" id="_x32_" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="#A4A7AE">
                                    <g id="SVGRepo_iconCarrier">
                                        <g>
                                            <path d="M474.234,284.88c23.29-23.268,37.781-55.654,37.766-91.185c0.015-35.53-14.476-67.917-37.766-91.178 c-23.261-23.298-55.64-37.788-91.185-37.766c-35.53-0.022-67.924,14.469-91.17,37.766c-23.304,23.261-37.795,55.648-37.78,91.178 c-0.015,35.53,14.476,67.916,37.78,91.185c19.268,19.297,44.809,32.517,73.21,36.488v52.976h-58.486v35.944h58.486v56.766h35.937 v-56.766h58.486v-35.944h-58.486v-52.976C429.426,317.397,454.966,304.177,474.234,284.88z M383.049,275.921 c-11.413-0.008-22.172-2.301-32.002-6.454c-14.724-6.222-27.327-16.684-36.198-29.817c-8.857-13.147-14.012-28.844-14.012-45.955 c0-11.42,2.294-22.172,6.447-31.994c6.214-14.731,16.669-27.326,29.81-36.198c13.155-8.864,28.85-14.019,45.955-14.034 c11.413,0.008,22.172,2.302,32.002,6.462c14.724,6.214,27.327,16.676,36.198,29.809c8.857,13.156,14.012,28.851,14.026,45.956 c-0.014,11.42-2.308,22.172-6.461,31.995c-6.214,14.73-16.683,27.333-29.81,36.198 C415.849,270.759,400.168,275.907,383.049,275.921z" />
                                            <path d="M146.912,202.305v-88.55l35.69,35.683l25.424-25.41l-79.075-79.082l-79.09,79.082l25.41,25.41l35.704-35.69 v88.557c-28.401,3.978-53.941,17.199-73.209,36.502C14.462,262.069-0.014,294.448,0,329.979 c-0.014,35.545,14.462,67.924,37.766,91.184c23.261,23.305,55.64,37.788,91.185,37.774c35.53,0.014,67.91-14.469,91.17-37.774 c23.304-23.261,37.78-55.64,37.78-91.184c0-35.53-14.476-67.91-37.78-91.171C200.854,219.505,175.313,206.283,146.912,202.305z M204.716,361.981c-6.229,14.73-16.683,27.326-29.81,36.198c-13.154,8.872-28.85,14.027-45.955,14.027 c-11.427,0-22.172-2.294-32.002-6.447c-14.723-6.229-27.327-16.684-36.198-29.817c-8.872-13.155-14.026-28.844-14.026-45.962 c0-11.412,2.294-22.164,6.446-31.987c6.229-14.723,16.684-27.326,29.824-36.198c13.14-8.864,28.836-14.011,45.955-14.026 c11.413,0,22.157,2.294,32.002,6.454c14.709,6.222,27.312,16.676,36.184,29.809c8.871,13.156,14.026,28.844,14.026,45.948 C211.163,341.405,208.868,352.165,204.716,361.981z" />
                                        </g>
                                    </g>
                                </svg>
                            </div>
                            <div className='display-inline-block font-size-10'>Gender Distribution</div>
                        </div>
                    </div>
                    <Bar data={genderChartData} options={optionsGender} />
                </div>
            </div>
            <div className='display-flex gap-10 mtb-10 sidbar-primary-text mt-20 mobile-flex-direction-column'>
                <div 
                    className="box-container" 
                    style={{ flex: 1, padding: 10, cursor: 'pointer' }}
                    onMouseMove={(e) => handleMouseMove(e, {
                        type: 'race',
                        races: processedData.raceData
                    })}
                    onMouseLeave={handleMouseLeave}
                >
                    <div className='text-align-left pl-5 mb-10'>Race Breakdown</div>
                    <div style={{ height: 260 }}>
                        <Bar data={raceChartData} options={optionsHorizontalBar} />
                    </div>
                </div>
                <div className="box-container" style={{ flex: 1, padding: 10 }}>
                    <div className='text-align-left pl-5 mb-10'>Income Breakdown</div>
                    <div style={{ height: 260 }}>
                        <Bar data={incomeChartData} options={optionsVerticalBar} />
                    </div>
                </div>
            </div>
            <div className='display-flex gap-10 mtb-10 sidbar-primary-text mt-20 mobile-flex-direction-column'>
                <div className="box-container" style={{ flex: 1, padding: 10 }}>
                    <div className='text-align-left pl-5 mb-10'>Age Breakdown</div>
                    <div style={{ height: 260 }}>
                        <Bar data={ageChartData} options={optionsVerticalBar} />
                    </div>
                </div>
                <div className="box-container" style={{ flex: 1, padding: 10 }}>
                    <div className='text-align-left pl-5 mb-10'>Other Locations Visited</div>
                    <UI_Table 
                        rows={processedData.storeVisitsData} 
                        titles={[
                            { title: 'In-Store Visits', key: 'title' }, 
                            { title: 'Percentage', key: 'percentage' }
                        ]} 
                    />
                </div>
            </div>
            
            {/* Custom Tooltip */}
            <CustomTooltip 
                data={tooltip.data} 
                position={tooltip.position} 
                visible={tooltip.visible} 
            />
        </div>
    );
}